import type { ShareTarget } from '@/types/shares'
import { withOrg } from '@/utils/orgLink'

/**
 * FS-663: the canonical link for a shared resource.
 *
 * These are ORDINARY app URLs, not capability links — there is no token. The
 * audience is company-only, so the link is useless without a session in the
 * owning organisation, and any colleague could reach the same URL anyway. What
 * sharing adds is discovery (it appears in their board list) and notification.
 *
 * `orgId` (the owning organisation) adds `?org=`, so a colleague who is a
 * member but has another organisation active is switched to it on open
 * (see `@/utils/orgLink`).
 *
 * Keep in step with `SharesService.resolveShareable`, which builds the same two
 * shapes server-side for the notification email.
 */
export function shareUrlPath(target: ShareTarget, orgId?: string | null): string {
  if (target.type === 'workspace') {
    return withOrg(`/board/${target.id}`, orgId)
  }
  // A chat lives inside its board. `/chat/:boardId` is the full-page chat
  // route; `?c=` selects which conversation to open once it loads.
  return withOrg(`/chat/${target.boardId}?c=${target.id}`, orgId)
}

/** Absolute URL for the copy-link affordance. */
export function shareUrl(target: ShareTarget, origin?: string, orgId?: string | null): string {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '')
  return `${base}${shareUrlPath(target, orgId)}`
}

/**
 * Deep link to a single message within a shared chat.
 *
 * The fragment targets the `data-message-id` attribute the message list already
 * renders, so no server round-trip is involved.
 */
export function shareMessageUrl(
  target: Extract<ShareTarget, { type: 'conversation' }>,
  messageId: string,
  origin?: string,
): string {
  return `${shareUrl(target, origin)}#m=${messageId}`
}

/** Human label for the kind of thing being shared, for copy and analytics. */
export function shareKindLabel(target: ShareTarget): 'board' | 'chat' {
  return target.type === 'workspace' ? 'board' : 'chat'
}
